import React, { FC, useMemo, useState } from 'react';
import { FormikProps } from 'formik';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { CustomButton } from '../../../../../components';
import { TournamentCategory } from '../../../../../types/tournament';
import { BracketCellGroup } from '../../../../../utils/groupMatchesByBracketCell';
import { getAvailableCategoriesForCell } from '../../../../../utils/bracketCellAvailableCategories.util';
import { formatTeamCategoryLabel } from '../../../../../utils/tournamentCategoryTemplate';
import { shouldOfferTiebreakerForClass } from '../../../../../utils/teamCategoryRubberCount';
import type { ClassValues } from '../../../TournamentClasses/components/ClassesTag/ClassesTag';
import type { PlayValues } from '../PlayTag/PlayTag';
import { useBracketCellMutations } from './useBracketCellMutations';

type BracketCellHeaderActionsProps = {
  group: BracketCellGroup;
  formikArg: FormikProps<PlayValues>;
  tournamentCategories: TournamentCategory[];
  currentClass?: ClassValues;
  variant?: 'group' | 'elimination';
};

const DECIDER_MATCH_TYPE = 'decider';

export const BracketCellHeaderActions: FC<BracketCellHeaderActionsProps> = ({
  group,
  formikArg,
  tournamentCategories,
  currentClass,
  variant = 'elimination',
}) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMatchType, setSelectedMatchType] = useState<number | typeof DECIDER_MATCH_TYPE | ''>(
    '',
  );
  const { handleAddCategoryMatch, handleAddDeciderMatch } = useBracketCellMutations(
    group,
    formikArg,
    t(prefix + 'bracketCellAddDeciderMatch'),
  );

  const availableCategories = useMemo(
    () => getAvailableCategoriesForCell(group.matches, tournamentCategories),
    [group.matches, tournamentCategories],
  );

  const canAddDecider = useMemo(() => {
    if (!shouldOfferTiebreakerForClass(tournamentCategories)) {
      return false;
    }
    if (group.matches.some((m) => m.isTiebreaker)) {
      return false;
    }
    if (variant === 'group') {
      return !!currentClass?.groupTiebreakerEnabled;
    }
    return !!currentClass?.tiebreakerEnabled;
  }, [currentClass, group.matches, tournamentCategories, variant]);

  const canAddMatch = canAddDecider || availableCategories.length > 0;

  const addButton = (
    <CustomButton
      variant="outline-dark"
      btnText={t(prefix + 'bracketCellAddCategoryMatch')}
      disabled={!canAddMatch}
      cb={() => setShowAddModal(true)}
      style={{ marginTop: 0, marginBottom: 0 }}
    />
  );

  return (
    <div className="d-flex flex-wrap align-items-center gap-2" data-testid="header-actions">
      {canAddMatch ? (
        addButton
      ) : (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`add-cat-${group.cellId}`}>
              {t(prefix + 'bracketCellAddCategoryDisabledHint')}
            </Tooltip>
          }
        >
          <span>{addButton}</span>
        </OverlayTrigger>
      )}

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t(prefix + 'bracketCellAddCategoryMatch')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label small">{t(prefix + 'bracketCellPickMatchType')}</label>
          <select
            className="form-select form-select-sm"
            value={selectedMatchType}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') {
                setSelectedMatchType('');
              } else if (v === DECIDER_MATCH_TYPE) {
                setSelectedMatchType(DECIDER_MATCH_TYPE);
              } else {
                setSelectedMatchType(Number(v));
              }
            }}
          >
            <option value="">{t(prefix + 'bracketCellPickMatchTypePlaceholder')}</option>
            {availableCategories.map((cat) => (
              <option key={cat.id} value={cat.id as number}>
                {formatTeamCategoryLabel(t, cat, cat.name)}
              </option>
            ))}
            {canAddDecider ? (
              <option value={DECIDER_MATCH_TYPE}>{t(prefix + 'bracketCellAddDeciderMatch')}</option>
            ) : null}
          </select>
        </Modal.Body>
        <Modal.Footer>
          <CustomButton
            variant="outline-secondary"
            btnText={t('modal-cancel')}
            cb={() => setShowAddModal(false)}
          />
          <CustomButton
            variant="primary"
            btnText={t('button-addMatch')}
            disabled={selectedMatchType === ''}
            cb={() => {
              if (selectedMatchType === DECIDER_MATCH_TYPE) {
                handleAddDeciderMatch();
                setShowAddModal(false);
                setSelectedMatchType('');
              } else if (typeof selectedMatchType === 'number') {
                handleAddCategoryMatch(selectedMatchType);
                setShowAddModal(false);
                setSelectedMatchType('');
              }
            }}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};
